import React from 'react';
import { useIntl } from 'react-intl';
import { AiOutlineGroup } from 'react-icons/ai';
import { CgTemplate } from 'react-icons/cg';
import { TbAsset } from 'react-icons/tb';
import { AssetNodeType } from '../models';
import { formatNodeCounts } from '../helpers';

const NEUTRAL_COLOR = '#6c757d';
const NEUTRAL_BG = '#f6f6f8';

const nodeTypeConfig = {
  [AssetNodeType.Template]: { color: NEUTRAL_COLOR, bgColor: NEUTRAL_BG, label: 'Template' },
  [AssetNodeType.Collection]: { color: NEUTRAL_COLOR, bgColor: NEUTRAL_BG, label: 'Collection' },
  [AssetNodeType.Group]: { color: NEUTRAL_COLOR, bgColor: NEUTRAL_BG, label: 'Group' },
  [AssetNodeType.Item]: { color: NEUTRAL_COLOR, bgColor: NEUTRAL_BG, label: 'Sub-Asset' },
};

interface AssetTreeNodeProps {
  node: any;
  depth: number;
  isExpanded: boolean;
  isSelected: boolean;
  isDropTarget?: boolean;
  onToggle: (nodeId: string) => void;
  onSelect: (nodeId: string) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
}

const AssetTreeNode: React.FC<AssetTreeNodeProps> = ({
  node,
  depth,
  isExpanded,
  isSelected,
  isDropTarget = false,
  onToggle,
  onSelect,
  onDragOver,
  onDragLeave,
  onDrop,
}) => {
  const intl = useIntl();
  const hasChildren = node.children && node.children.length > 0;
  const config = nodeTypeConfig[node.type] || nodeTypeConfig[AssetNodeType.Item];
  const counts = formatNodeCounts(node);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasChildren) {
      onToggle(node.id);
    }
  };

  const handleSelect = () => {
    onSelect(node.id);
  };

  return (
    <div className="asset-tree-node-wrapper">
      <div
        className={`asset-tree-node ${isSelected ? 'asset-tree-node--selected' : ''}${
          isDropTarget ? ' asset-tree-node--drop-target' : ''
        }`}
        style={{ paddingLeft: `${depth * 16 + 12}px` }}
        onClick={handleSelect}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        role="treeitem"
        aria-expanded={hasChildren ? isExpanded : undefined}
        aria-selected={isSelected}
      >
        {hasChildren ? (
          <button
            className="asset-tree-node__toggle"
            onClick={handleToggle}
            aria-label={
              isExpanded
                ? intl.formatMessage({ id: 'assets.tree.collapse' })
                : intl.formatMessage({ id: 'assets.tree.expand' })
            }
          >
            <i className={`uil ${isExpanded ? 'uil-angle-down' : 'uil-angle-right'}`} />
          </button>
        ) : (
          <span className="asset-tree-node__toggle-spacer" />
        )}

        {node.type === AssetNodeType.Template ? (
          <CgTemplate className="asset-tree-node__icon" size={16} color={config.color} />
        ) : node.type === AssetNodeType.Group ? (
          <AiOutlineGroup className="asset-tree-node__icon" size={16} color={config.color} />
        ) : (
          <TbAsset className="asset-tree-node__icon" size={16} color={config.color} />
        )}

        <div className="asset-tree-node__content">
          <div className="asset-tree-node__name">{node.name}</div>
          <div className="asset-tree-node__meta">
            <span
              className="asset-tree-node__type-badge"
              style={{ color: config.color, backgroundColor: config.bgColor }}
            >
              {config.label}
            </span>
          </div>
          {counts && <div className="asset-tree-node__counts">{counts}</div>}
        </div>
      </div>
    </div>
  );
};

export default AssetTreeNode;
